import { Navigate, Outlet, useLocation } from '@tanstack/react-router';
import { useMe, useRefreshMe, useStopImpersonation, useTwoFactorActions } from '@blimo/api-client/react';
import { getErrorMessage } from '@blimo/api-client';
import { Alert, AlertDescription, Button } from '@blimo/ui';
import { AuthLayout, FullPageSpinner, NoAccessPage, TwoFactorRequiredPage } from '@blimo/ui/auth';
import { ExternalLink, UserRoundCog } from 'lucide-react';
import { AppShell } from '../layout/app-shell.jsx';
import { useLogout } from '../lib/use-logout.js';
import { env } from '../lib/api.js';
import { useAppName } from '../lib/app-name.js';

/**
 * Guards every page behind login. The route's beforeLoad already redirected
 * anonymous users; this component reacts to later changes of /me.
 */
export function ProtectedLayout() {
  const appName = useAppName();
  const { data: me, error, isPending, refetch } = useMe();
  const location = useLocation();
  const logout = useLogout();
  const twoFactorActions = useTwoFactorActions();
  const refreshMe = useRefreshMe();

  if (isPending) return <FullPageSpinner />;

  if (error) {
    if (error.status === 401) return <Navigate to="/login" search={{ redirect: location.href }} replace />;
    if (error.isTwoFactorRequired) return <TwoFactorRequiredPage appName={appName} actions={twoFactorActions} onDone={refreshMe} onLogout={logout} />;
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 p-6">
        <Alert variant="destructive">
          <AlertDescription>{getErrorMessage(error, 'Die Anwendung konnte nicht geladen werden.')}</AlertDescription>
        </Alert>
        <Button onClick={() => refetch()}>Erneut versuchen</Button>
      </div>
    );
  }

  if (me.two_factor?.required && !me.two_factor?.enabled) {
    return <TwoFactorRequiredPage appName={appName} actions={twoFactorActions} onDone={refreshMe} onLogout={logout} />;
  }

  if (!me.apps?.admin) {
    if (me.impersonating) return <ImpersonatingPortalUserPage me={me} />;
    return (
      <NoAccessPage appName={appName} name={me.name} otherApp={me.apps?.portal ? { label: 'Zum Kundenportal', href: env.portalUrl } : null} onLogout={logout} />
    );
  }

  return (
    <AppShell me={me}>
      <Outlet />
    </AppShell>
  );
}

/**
 * After „Als Benutzer anmelden“ the session belongs to a (usually portal-only) user:
 * the admin UI is not available, so offer the portal and a way back.
 */
function ImpersonatingPortalUserPage({ me }) {
  const stop = useStopImpersonation();
  const appName = useAppName();
  return (
    <AuthLayout appName={appName} title="Benutzeransicht aktiv">
      <div className="flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <UserRoundCog className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">
            Sie handeln als <strong className="text-foreground">{me.name}</strong>. Dieses Konto hat keinen Zugang zur Verwaltung. Alle Aktionen werden mit
            Ihrem Admin-Konto protokolliert.
          </p>
        </div>
        {me.apps?.portal && (
          <Button asChild>
            <a href={env.portalUrl} target="_blank" rel="noreferrer">
              <ExternalLink aria-hidden="true" /> Portal öffnen
            </a>
          </Button>
        )}
        <Button variant="outline" onClick={() => stop.mutate()} loading={stop.isPending}>
          Benutzeransicht beenden
        </Button>
        {stop.error && (
          <Alert variant="destructive">
            <AlertDescription>{getErrorMessage(stop.error)}</AlertDescription>
          </Alert>
        )}
      </div>
    </AuthLayout>
  );
}
